/**
 * AnimatedBeam – draws a curved animated beam between two element refs (Magic UI style).
 * Uses quadratic Bézier path and stroke-dashoffset animation; direction can be reversed at random.
 */
import React, { useLayoutEffect, useState, useId, useEffect } from "react";
import { Box } from "@mui/material";

const BEAM_KEYFRAMES_ID = "animated-beam-keyframes";
const KEYFRAMES_CSS = `
@keyframes animatedBeamTravel {
  0% { stroke-dashoffset: 0; }
  100% { stroke-dashoffset: -414; }
}`;

function getCenter(rect) {
  return {
    x: rect.left + rect.width / 2,
    y: rect.top + rect.height / 2,
  };
}

export default function AnimatedBeam({
  containerRef,
  fromRef,
  toRef,
  curvature = 0,
  duration = 3,
  reverse = false,
  startXOffset = 0,
  startYOffset = 0,
  endXOffset = 0,
  endYOffset = 0,
  pathColor = "primary.main",
  pathOpacity = 0.85,
  pathWidth = 3,
  open,
  delay = 0,
  layoutDeps,
}) {
  const [path, setPath] = useState("");
  const [size, setSize] = useState({ w: 0, h: 0 });
  const gradientId = useId().replace(/:/g, "");

  useEffect(() => {
    if (document.getElementById(BEAM_KEYFRAMES_ID)) return;
    const style = document.createElement("style");
    style.id = BEAM_KEYFRAMES_ID;
    style.textContent = KEYFRAMES_CSS;
    document.head.appendChild(style);
  }, []);

  useLayoutEffect(() => {
    if (!containerRef?.current || !fromRef?.current || !toRef?.current) return;
    const container = containerRef.current;
    const fromEl = fromRef.current;
    const toEl = toRef.current;

    const update = () => {
      const cRect = container.getBoundingClientRect();
      const fromRect = fromEl.getBoundingClientRect();
      const toRect = toEl.getBoundingClientRect();
      const start = getCenter(fromRect);
      const end = getCenter(toRect);
      const startX = start.x - cRect.left + startXOffset;
      const startY = start.y - cRect.top + startYOffset;
      const endX = end.x - cRect.left + endXOffset;
      const endY = end.y - cRect.top + endYOffset;
      const midX = (startX + endX) / 2;
      const midY = (startY + endY) / 2 - curvature;
      const d = `M ${startX} ${startY} Q ${midX} ${midY} ${endX} ${endY}`;
      setPath(d);
      setSize({ w: cRect.width, h: cRect.height });
    };

    update();
    const raf = requestAnimationFrame(update);
    const ro = new ResizeObserver(update);
    ro.observe(container);
    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
    };
  }, [containerRef, fromRef, toRef, curvature, startXOffset, startYOffset, endXOffset, endYOffset, open, layoutDeps]);

  useEffect(() => {
    if (!open || !containerRef?.current || !fromRef?.current || !toRef?.current) return;
    const t = setTimeout(() => {
      if (!containerRef?.current || !fromRef?.current || !toRef?.current) return;
      const container = containerRef.current;
      const cRect = container.getBoundingClientRect();
      const fromRect = fromRef.current.getBoundingClientRect();
      const toRect = toRef.current.getBoundingClientRect();
      const start = getCenter(fromRect);
      const end = getCenter(toRect);
      const startX = start.x - cRect.left + startXOffset;
      const startY = start.y - cRect.top + startYOffset;
      const endX = end.x - cRect.left + endXOffset;
      const endY = end.y - cRect.top + endYOffset;
      const midX = (startX + endX) / 2;
      const midY = (startY + endY) / 2 - curvature;
      setPath(`M ${startX} ${startY} Q ${midX} ${midY} ${endX} ${endY}`);
      setSize({ w: cRect.width, h: cRect.height });
    }, 50);
    return () => clearTimeout(t);
  }, [open, containerRef, fromRef, toRef, curvature, startXOffset, startYOffset, endXOffset, endYOffset, layoutDeps]);

  if (!path || size.w === 0) return null;

  return (
    <>
      <Box
        component="svg"
        viewBox={`0 0 ${size.w} ${size.h}`}
        preserveAspectRatio="none"
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          pointerEvents: "none",
          overflow: "visible",
          zIndex: 1,
        }}
      >
        <defs>
          <linearGradient
            id={`beam-${gradientId}`}
            x1="0%"
            y1="0%"
            x2="100%"
            y2="0%"
            gradientUnits="userSpaceOnUse"
          >
            <stop offset="0%" stopColor={pathColor} stopOpacity="0.3" />
            <stop offset="50%" stopColor={pathColor} stopOpacity={pathOpacity} />
            <stop offset="100%" stopColor={pathColor} stopOpacity="0.3" />
          </linearGradient>
        </defs>
        {/* Curved track (visible path) */}
        <path
          d={path}
          fill="none"
          stroke="rgba(27, 69, 132, 0.2)"
          strokeWidth={pathWidth + 2}
          strokeLinecap="round"
        />
        {/* Animated beam along curve – inline style so animation applies to native SVG path */}
        <path
          d={path}
          fill="none"
          stroke={`url(#beam-${gradientId})`}
          strokeWidth={pathWidth}
          strokeLinecap="round"
          strokeDasharray="14 400"
          style={{
            animationName: "animatedBeamTravel",
            animationDuration: `${duration}s`,
            animationTimingFunction: "ease-in-out",
            animationIterationCount: "infinite",
            animationDirection: reverse ? "reverse" : "normal",
            animationDelay: `${delay}s`,
          }}
        />
      </Box>
    </>
  );
}
